import styled from '@emotion/styled';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import { breakpoints, Spacings } from '@styles/index';
import Paper from '@base/Paper/Paper';
import H2 from '@base/H2/H2';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import H4 from '@base/H4/H4';
import { getImage } from '@utils/imageHelper';
import SEO from '@seo/index';
import { getIsGTPGoal } from '@api/selectors/constants';
import escape from '@utils/escapeHTML';
import useConstants from '@api/hooks/useConstants';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import FreePracticeBanner from './FreePracticeBanner';
import FreeQuiz from './FreeQuiz';
import TopicCard from './TopicCard';

const Container = styled.div`
  grid-column: span 18;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-column: span 10;
    margin-bottom: 16px;
  }
`;

const Header = styled(Paper)`
  margin-bottom: ${Spacings.SPACING_10B};
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_8B} ${Spacings.SPACING_12B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    background-color: var(--color-base-0);
    box-shadow: none;
    padding: 24px 0 0;
  }
`;

const HeaderTitle = styled(H2)`
  margin-top: ${Spacings.SPACING_4B};
  color: var(--color-text-primary);
`;

const Concepts = styled.div`
  margin-bottom: 40px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 16px;
  @media only screen and (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    margin-bottom: 56px;
  }
`;

const ConceptsTitle = styled(H4)`
  margin-bottom: 32px;
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 20px;
    line-height: 120%;
  }
`;

const StyledTopicCard = styled(TopicCard)`
  display: flex;
  align-items: center;
  padding-left: 32px;
`;

function FreePracticeTopic() {
  const router = useRouter();
  const asLink = router.asPath;
  const { goalUID, topicUID } = router.query;
  const goalDetails = useSelector(({ topologies }) => topologies.data[goalUID]);
  const topics = useSelector(
    ({ freePracticeSession }) => freePracticeSession.topics
  );

  const childConcepts = topics.children_concepts?.filter(
    (concept) => concept.practice_session_exists === true
  );
  const isGTP = useConstants(getIsGTPGoal(goalUID));
  const hasConcepts = childConcepts?.length > 0;
  const metaTitle = `Practice ${topics.name} - ${goalDetails.name} Offered by
  Unacademy`;
  const metaDescription = `Practice the concepts of ${topics.name} - ${goalDetails.name}`;
  const canonicalURL = `https://unacademy.com/goal/${goalDetails.slug}/${
    goalDetails.uid
  }/practice/topics/${escape(topicUID)}`;
  const breadCrumb = [
    {
      title: goalDetails.name,
      href: '/goal/[goalSlug]/[goalUID]',
      as: goalDetails.relativeLink
    },
    {
      title: goalUID === 'SDDOC' ? plusNavBarItems.Q_BANK : 'Practice',
      href: '/goal/[goalSlug]/[goalUID]/practice/topics',
      as: `${goalDetails.relativeLink}/practice/topics`
    },
    {
      title: topics.parent_name,
      href: '/goal/[goalSlug]/[goalUID]/practice/topics',
      as: `${goalDetails.relativeLink}/practice/topics`
    }
  ];

  return (
    <Container>
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={canonicalURL}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          canonicalURL
        }}
      />
      <Header gridColumn="span 18">
        <BreadCrumbs tags={breadCrumb} onBackClick={router.back} useBackClick />
        <HeaderTitle>{topics.name}</HeaderTitle>
      </Header>
      <FreeQuiz topicName={topics.parent_name} goalName={goalDetails.name} />
      {!isGTP && hasConcepts && (
        <>
          <ConceptsTitle>Concepts</ConceptsTitle>
          <Concepts>
            {childConcepts.map((concept) => (
              <StyledTopicCard
                href="/goal/[goalSlug]/[goalUID]/practice/topics/[topicUid]/concept/[conceptUID]"
                asLink={`${asLink}/concept/${concept.uid}`}
                topic={concept}
                displayConcepts={false}
                displayIcon={getImage('practice-session/topics/notes.svg')}
                key={concept.uid}
              />
            ))}
          </Concepts>
        </>
      )}

      <FreePracticeBanner topicName={topics.parent_name} />
    </Container>
  );
}

export default FreePracticeTopic;
